import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { FilterType } from '~/components/shared'
import { createBirthReportEvent, HealthProcedureVariantProps } from '~/constants'
import { useFetchHealthProcedures, useFetchPatientPregnancies } from '~/hooks'
import {
  createDateFilterTagWith,
  EMedicalViews,
  IFetchHealthProceduresParams,
  IFetchPregnancy,
  IFilterTag,
  IHealthProcedures,
  IPregnancy,
  ITimelineMaternalInfant,
  TimelineMaternalInfantPeriodType,
  IGetPatientPregnanciesParams,
  createTypeFiltersTagWith,
  createSearchTermTagWith
} from '~/interfaces'
import { removeUnsupportedEvents } from '~/services'
import { hasObjectValue, identifyInProgressEvents, timelineFilters, transformDate } from '~/utils'

import {
  getGestationRangeBy,
  getInfantRangeBy,
  getNeonateRangeBy,
  getPuerperiumRangeBy,
  identifyDischargeNotebook
} from '~/utils/dashboard/timeline/maternalInfant'

interface MaternalInfantHealthProceduresProviderProps {
  children: React.ReactNode
}

const defaultValue = {
  healthProcedures: [] as IHealthProcedures[],
  filterTags: [] as IFilterTag[],
  selectedPeriod: 'trimester' as TimelineMaternalInfantPeriodType,
  selectedTimeline: 'mother' as ITimelineMaternalInfant.PatientType,

  selectedPregnancy: {} as IFetchPregnancy
}

export interface IMaternalInfantHealthProceduresContextHelpers {
  isLoadingTimeline: boolean
  errorTimeline: string

  isPatientPregnanciesLoading: boolean
  fetchPregnanciesError: string | boolean
  thereIsFetchPregnanciesError: () => boolean
}

export interface IMaternalInfantHealthProceduresContextProps extends IMaternalInfantHealthProceduresContextHelpers {
  getBabyHealthProcedures: (params: IFetchHealthProceduresParams) => void
  getMotherHealthProcedures: (params: IFetchHealthProceduresParams) => void

  gestationHealthProcedures: IHealthProcedures[]
  puerperiumHealthProcedures: IHealthProcedures[]
  neonateHealthProcedures: IHealthProcedures[]
  infantHealthProcedures: IHealthProcedures[]

  getHealthProcedures: () => IHealthProcedures[]
  selectedTimeline: ITimelineMaternalInfant.PatientType
  updateSelectedTimeline: (timeline: ITimelineMaternalInfant.PatientType) => void
  toggleSelectedTimeline: () => void
  changeToBabyTimeline: () => void
  isMotherTimelineSelected: boolean
  isBabyTimelineSelected: boolean

  selectedPregnancy: IFetchPregnancy
  updateSelectedPregnancy: (params: IFetchPregnancy) => void

  selectedBaby: number | null
  updateSelectedBaby: (childId: number) => void
  updateDefaultSelectedBaby: () => void

  modalHealthProcedure: IHealthProcedures | null
  handleFilterHealthProceduresPerType: (types: FilterType[]) => void
  handleFilterHealthProceduresPerDate: (rangeDates: Date[]) => void
  handleSelectedModalHealthProcedures: (healthProcedures: IHealthProcedures | null) => void

  dateFiltersActive: Date[]
  typeFiltersActive: string[]

  openConductCardList: boolean
  setOpenConductCardList: Dispatch<SetStateAction<boolean>>
  openOmicsCardList: boolean
  setOpenOmicsCardList: Dispatch<SetStateAction<boolean>>

  hasDateFiltersActive: boolean
  hasTypeFiltersActive: boolean

  filterTags: IFilterTag[]
  addFilterTag: (newTag: IFilterTag) => void
  removeFilterTag: (tag: IFilterTag) => void
  resetFilterTags: () => void
  filterHealthProceduresByTerm: (term: IFilterTag) => void

  pregnancies: IPregnancy[]
  getPatientPregnancies: (params: IGetPatientPregnanciesParams) => void
  thereArePregnancies: () => boolean
  thereAreNoPregnancies: () => boolean

  sidePanelEvents: IHealthProcedures[]
  updateSidePanelEvents: (events: IHealthProcedures[]) => void
  resetSidePanelEvents: () => void
  thereAreNoSelectedPregnancy: () => boolean
}

const MaternalInfantHealthProceduresContext = React.createContext<IMaternalInfantHealthProceduresContextProps>(
  {} as IMaternalInfantHealthProceduresContextProps
)

const MaternalInfantHealthProceduresProvider = ({
  children
}: MaternalInfantHealthProceduresProviderProps): JSX.Element => {
  const [puerperiumHealthProcedures, setPuerperiumHealthProcedures] = useState<IHealthProcedures[]>(
    defaultValue.healthProcedures
  )
  const [gestationHealthProcedures, setGestationHealthProcedures] = useState<IHealthProcedures[]>(
    defaultValue.healthProcedures
  )
  const [neonateHealthProcedures, setNeonateHealthProcedures] = useState<IHealthProcedures[]>(
    defaultValue.healthProcedures
  )
  const [infantHealthProcedures, setInfantHealthProcedures] = useState<IHealthProcedures[]>(
    defaultValue.healthProcedures
  )

  const [modalHealthProcedure, setModalHealthProcedure] = useState<IHealthProcedures | null>(null)

  const [selectedPregnancy, setSelectedPregnancy] = useState<IFetchPregnancy>(defaultValue.selectedPregnancy)
  const [selectedBaby, setSelectedBaby] = useState<number | null>(null)

  const [selectedTimeline, setSelectedTimeline] = useState<ITimelineMaternalInfant.PatientType>(
    defaultValue.selectedTimeline
  )
  const [openConductCardList, setOpenConductCardList] = useState<boolean>(false)
  const [openOmicsCardList, setOpenOmicsCardList] = useState<boolean>(false)
  const [dateFiltersActive, setDateFiltersActive] = useState<Date[]>([])
  const [typeFiltersActive, setTypeFiltersActive] = useState<string[]>([])

  const [filterTags, setFilterTags] = useState<IFilterTag[]>(defaultValue.filterTags)

  const getHealthProcedures = useCallback((): IHealthProcedures[] => {
    const groupedEvents = [
      ...gestationHealthProcedures,
      ...puerperiumHealthProcedures,
      ...neonateHealthProcedures,
      ...infantHealthProcedures
    ]

    const orderedEvents = groupedEvents.sort((a, b) => {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    })

    return orderedEvents
  }, [gestationHealthProcedures, puerperiumHealthProcedures, neonateHealthProcedures, infantHealthProcedures])

  const [sidePanelEvents, setSidePanelEvents] = useState<IHealthProcedures[]>([])
  const updateSidePanelEvents = useCallback((events: IHealthProcedures[]) => setSidePanelEvents(events), [])
  const resetSidePanelEvents = useCallback(() => {
    const allEvents = getHealthProcedures()
    updateSidePanelEvents(allEvents)
  }, [getHealthProcedures, updateSidePanelEvents])

  const initializeSidePanel = useCallback(() => {
    resetSidePanelEvents()
  }, [resetSidePanelEvents])

  useEffect(() => {
    if (getHealthProcedures().length > 0) initializeSidePanel()
  }, [getHealthProcedures, initializeSidePanel])

  const filterHealthProceduresForBabyView = (gestation: IFetchPregnancy, healthProcedures: IHealthProcedures[]) => {
    const healthProceduresWithDischargedNotebook = identifyDischargeNotebook(healthProcedures)

    const neonateRange = getNeonateRangeBy(gestation)
    const neonateEvents = timelineFilters(healthProceduresWithDischargedNotebook).perEventStartDate(neonateRange)

    const infantRange = getInfantRangeBy(gestation)
    const infantEvents = timelineFilters(healthProceduresWithDischargedNotebook).perEventEndDate(infantRange)

    setNeonateHealthProcedures(neonateEvents.value)
    setInfantHealthProcedures(infantEvents.value)
  }

  const [babyFetchParams, setBabyFetchParams] = useState<IFetchHealthProceduresParams | null>(null)
  const {
    data: babyHealthProcedures,
    isLoading: babyTimelineLoader,
    error: babyTimelineError
  } = useFetchHealthProcedures(babyFetchParams, EMedicalViews.MATERNAL_INFANT)

  const treatFetchedBabyEvents = useCallback((healthProcedures: IHealthProcedures[]) => {
    const validEvents = removeUnsupportedEvents(healthProcedures)
    const inProgressEvents = identifyInProgressEvents(validEvents)
    return inProgressEvents
  }, [])

  useEffect(() => {
    if (babyHealthProcedures) {
      const treatedHealthProcedures = treatFetchedBabyEvents(babyHealthProcedures)
      filterHealthProceduresForBabyView(selectedPregnancy, treatedHealthProcedures)
    }
  }, [babyHealthProcedures, treatFetchedBabyEvents])

  const getBabyHealthProcedures = useCallback((params: IFetchHealthProceduresParams) => {
    setBabyFetchParams(params)
  }, [])

  const filterGestationHealthProcedures = (gestation: IPregnancy, healthProcedures: IHealthProcedures[]) => {
    const gestationRange = getGestationRangeBy(gestation)
    const gestationEvents = timelineFilters(healthProcedures).perEventStartDate(gestationRange)
    setGestationHealthProcedures(gestationEvents.value)
  }

  const filterPuereperiumHealthProcedures = (gestation: IPregnancy, healthProcedures: IHealthProcedures[]) => {
    const puerperiumRange = getPuerperiumRangeBy(gestation)
    const puerperiumEvents = timelineFilters(healthProcedures).perEventEndDate(puerperiumRange)
    setPuerperiumHealthProcedures(puerperiumEvents.value)
  }

  const filterHealthProceduresForMotherView = (gestation: IFetchPregnancy, healthProcedures: IHealthProcedures[]) => {
    filterPuereperiumHealthProcedures(gestation, healthProcedures)
    filterGestationHealthProcedures(gestation, healthProcedures)
  }

  const [motherFetchParams, setMotherFetchParams] = useState<IFetchHealthProceduresParams | null>(null)
  const {
    data: motherHealthProcedures,
    isLoading: motherTimelineLoader,
    error: motherTimelineError
  } = useFetchHealthProcedures(motherFetchParams, EMedicalViews.MATERNAL_INFANT)

  const treatFetchedMotherEvents = useCallback(
    (healthProcedures: IHealthProcedures[]) => {
      const validEvents = removeUnsupportedEvents(healthProcedures)
      const inProgressEvents = identifyInProgressEvents(validEvents)

      return selectedTimeline === 'baby' && selectedBaby
        ? injectBirthReportInto(selectedPregnancy, selectedBaby, inProgressEvents)
        : inProgressEvents
    },
    [selectedBaby, selectedPregnancy, selectedTimeline]
  )

  useEffect(() => {
    if (motherHealthProcedures) {
      const treatedHealthProcedures = treatFetchedMotherEvents(motherHealthProcedures)
      filterHealthProceduresForMotherView(selectedPregnancy, treatedHealthProcedures)
    }
  }, [motherHealthProcedures, selectedPregnancy, treatFetchedMotherEvents])

  const getMotherHealthProcedures = useCallback((params: IFetchHealthProceduresParams) => {
    setMotherFetchParams(params)
  }, [])

  const updateTypeFilterTags = useCallback((filters: FilterType[]): void => {
    const tagsOfTypeFilters = filters.map(filter => {
      const filterColor = HealthProcedureVariantProps[filter.type[0]].color
      return createTypeFiltersTagWith(filter.text, filterColor, filter.type)
    })

    setFilterTags(existingTags => {
      const existentTags = existingTags.filter(tag => tag.type !== 'typeFilter')
      return [...existentTags, ...tagsOfTypeFilters]
    })
  }, [])

  const handleFilterHealthProceduresPerType = useCallback(
    (typeFilters: FilterType[]) => {
      const activeTypes = typeFilters.flatMap(filter => filter.type)
      setTypeFiltersActive(activeTypes)
      updateTypeFilterTags(typeFilters)
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
    },
    [updateTypeFilterTags, initializeSidePanel]
  )

  const removeTypeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      const newTypeFiltersActive = typeFiltersActive.filter(type => {
        const tagTypes = tag.id.split('-')
        return !tagTypes.includes(type)
      })
      setTypeFiltersActive(newTypeFiltersActive)
    },
    [typeFiltersActive]
  )

  const hasTypeFiltersActive: boolean = typeFiltersActive.length > 0

  const updateDateFilterTags = useCallback(
    (rangeDates: Date[]): void => {
      const tagsWithoutDateFilter = filterTags.filter(tag => tag.type !== 'dateFilter')
      const isNotAValidRangeOfDates = rangeDates.length <= 1
      if (isNotAValidRangeOfDates) {
        setFilterTags(tagsWithoutDateFilter)
        return
      }

      const formattedDates = rangeDates.map(date => transformDate({ date }))
      const tagDescription = formattedDates.join(' - ')
      setFilterTags([...tagsWithoutDateFilter, createDateFilterTagWith(tagDescription)])
    },
    [filterTags]
  )

  const handleFilterHealthProceduresPerDate = useCallback(
    (rangeDates: Date[]) => {
      const isAValidRangeOfDates = rangeDates.length !== 1

      if (isAValidRangeOfDates) {
        setDateFiltersActive(rangeDates)
        updateDateFilterTags(rangeDates)
        initializeSidePanel()
        setOpenOmicsCardList(true)
        setOpenConductCardList(true)
      }
    },
    [updateDateFilterTags, initializeSidePanel]
  )

  const removeDateFilterTag = (): void => {
    setDateFiltersActive([])
  }

  const hasDateFiltersActive: boolean = dateFiltersActive.length > 0

  const handleSelectedModalHealthProcedures = (healthProcedures: IHealthProcedures | null) => {
    setModalHealthProcedure(healthProcedures)
  }

  const updateSelectedTimeline = useCallback(
    (timeline: ITimelineMaternalInfant.PatientType): void => setSelectedTimeline(timeline),
    []
  )
  const toggleSelectedTimeline = () => {
    const newTimeline = selectedTimeline === 'mother' ? 'baby' : 'mother'
    updateSelectedTimeline(newTimeline)
  }
  const changeToBabyTimeline = () => updateSelectedTimeline('baby')
  const isMotherTimelineSelected = useMemo(() => selectedTimeline === 'mother', [selectedTimeline])
  const isBabyTimelineSelected = useMemo(() => selectedTimeline === 'baby', [selectedTimeline])

  const updateSelectedPregnancy = useCallback((newPregnancy: IFetchPregnancy) => {
    setSelectedPregnancy({ ...newPregnancy })
  }, [])

  const updateSelectedBaby = useCallback((childId: number): void => {
    setSelectedBaby(childId)
  }, [])

  const updateDefaultSelectedBaby = (): void => {
    const { children } = selectedPregnancy
    const hasChildren = children && children.length > 0
    if (hasChildren) {
      setSelectedBaby(children[0].id)
    }
  }

  const addFilterTag = (newTag: IFilterTag): void => {
    setFilterTags(currentTags => [...currentTags, { ...newTag }])
  }

  const removeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      setFilterTags(existingTags => existingTags.filter(ft => ft.id !== tag.id))

      switch (tag.type) {
        case 'dateFilter':
          return removeDateFilterTag()
        case 'typeFilter':
          return removeTypeFilterTag(tag)
      }
    },
    [removeTypeFilterTag]
  )

  const resetFilterTags = useCallback((): void => {
    setFilterTags(defaultValue.filterTags)

    setTypeFiltersActive([])
    removeDateFilterTag()
  }, [])

  const loaderTimelineState = (babyLoaderState: boolean, motherLoaderState: boolean): boolean => {
    const isLoadingBabyTimeline = babyLoaderState && isBabyTimelineSelected
    const isLoadingMotherTimeline = motherLoaderState && isMotherTimelineSelected

    if (isLoadingMotherTimeline || isLoadingBabyTimeline) {
      return true
    }

    return false
  }

  const errorTimelineState = (babyErrorState: Error | null, motherErrorState: Error | null): string => {
    const isErrorBabyTimeline = babyErrorState?.message && isBabyTimelineSelected
    const isErrorMotherTimeline = motherErrorState?.message && isMotherTimelineSelected

    if (isErrorMotherTimeline) {
      return motherErrorState.message
    }

    if (isErrorBabyTimeline) {
      return babyErrorState.message
    }

    return ''
  }

  const filterHealthProceduresByTerm = useCallback(
    (term: IFilterTag) => {
      const tagByTerm = createSearchTermTagWith(term.text)
      setFilterTags(existingTags => [...existingTags, tagByTerm])
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
    },
    [initializeSidePanel]
  )

  const injectBirthReportInto = (gestation: IPregnancy, childId: number, healthProcedures: IHealthProcedures[]) => {
    const birthReportEvent = createBirthReportEvent({ ...gestation, childId })
    const motherGestationHealthProcedures = [...(healthProcedures ?? []), birthReportEvent]
    return motherGestationHealthProcedures
  }

  const [pregnanciesFetchParams, setPregnanciesFetchParams] = useState<IGetPatientPregnanciesParams | null>(null)
  const {
    data: pregnancies,
    isLoading: isPatientPregnanciesLoading,
    error: fetchPregnanciesError
  } = useFetchPatientPregnancies(pregnanciesFetchParams)

  const thereArePregnancies = () => !!(pregnancies && pregnancies?.length > 0)
  const thereAreNoPregnancies = () => !pregnancies || pregnancies?.length === 0
  const thereAreNoSelectedPregnancy = () => !hasObjectValue({ ...selectedPregnancy })
  const thereIsFetchPregnanciesError = () => fetchPregnanciesError instanceof Error

  const getPatientPregnancies = useCallback(async (params: IGetPatientPregnanciesParams) => {
    setPregnanciesFetchParams(params)
  }, [])

  return (
    <MaternalInfantHealthProceduresContext.Provider
      value={{
        getBabyHealthProcedures,
        getMotherHealthProcedures,
        gestationHealthProcedures,
        puerperiumHealthProcedures,
        neonateHealthProcedures,
        infantHealthProcedures,

        getHealthProcedures,

        selectedTimeline,
        updateSelectedTimeline,
        toggleSelectedTimeline,
        changeToBabyTimeline,
        isMotherTimelineSelected,
        isBabyTimelineSelected,

        selectedPregnancy,
        updateSelectedPregnancy,

        selectedBaby,
        updateSelectedBaby,
        updateDefaultSelectedBaby,

        modalHealthProcedure,
        handleFilterHealthProceduresPerType,
        handleFilterHealthProceduresPerDate,
        handleSelectedModalHealthProcedures,

        errorTimeline: errorTimelineState(babyTimelineError, motherTimelineError),
        isLoadingTimeline: loaderTimelineState(babyTimelineLoader, motherTimelineLoader),

        dateFiltersActive,
        typeFiltersActive,

        openConductCardList,
        setOpenConductCardList,
        openOmicsCardList,
        setOpenOmicsCardList,

        filterTags,
        addFilterTag,
        removeFilterTag,
        resetFilterTags,
        filterHealthProceduresByTerm,

        hasDateFiltersActive,
        hasTypeFiltersActive,

        pregnancies: pregnancies ?? [],
        getPatientPregnancies,
        thereArePregnancies,
        thereAreNoPregnancies,
        thereAreNoSelectedPregnancy,
        isPatientPregnanciesLoading,
        fetchPregnanciesError: fetchPregnanciesError?.message ?? false,
        thereIsFetchPregnanciesError,

        sidePanelEvents,
        updateSidePanelEvents,
        resetSidePanelEvents
      }}
    >
      {children}
    </MaternalInfantHealthProceduresContext.Provider>
  )
}

const useMaternalInfantHealthProcedures = (): IMaternalInfantHealthProceduresContextProps => {
  return React.useContext(MaternalInfantHealthProceduresContext)
}

export {
  MaternalInfantHealthProceduresContext,
  MaternalInfantHealthProceduresProvider,
  useMaternalInfantHealthProcedures
}
